import { useState } from 'react';
import { toast } from 'react-toastify';
import { ManagedAccountsApi } from '@/api/clients/managed-accounts/managed-accounts.api';
import useFormValidation from '@/hooks/useFormValidation';
import {
  MANAGED_ACCOUNT_PROVIDER,
  MANAGED_ACCOUNT_TYPE,
  SFTP_AUTHENTICATION_MODE,
} from '../managedAccount.constants';
import { useReconPaths } from './useReconPaths';

const emptyForm = {
  name: '',
  type: MANAGED_ACCOUNT_TYPE.PAYMENTS_MANAGER,
  provider: MANAGED_ACCOUNT_PROVIDER.WELLS_FARGO,
  host: '',
  port: '22',
  username: '',
  authentication_mode: SFTP_AUTHENTICATION_MODE.PASSWORD,
  password: '',
  private_key: '',
  public_key: '',
  outbound_path: '',
  inbound_path: '',
  payments_manager_prefix: '',
  ack_prefix: '',
  notification_prefix: '',
  recon_prefix: '',
  positive_pay_prefix: '',
  header_account: '',
  should_encrypt: false,
  pgp_public_key: '',
};

const toSftpPayload = (form) => ({
  host: form.host,
  port: Number(form.port),
  username: form.username,
  authentication_mode: form.authentication_mode,
  password: form.password,
  private_key: form.private_key,
  public_key: form.public_key,
});

export const useCreateManagedAccountForm = ({ onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [createdAccount, setCreatedAccount] = useState(null);
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearFieldError,
    clearAllErrors,
  } = useFormValidation();
  const recon = useReconPaths([], clearFieldError);

  const reset = () => {
    setForm(emptyForm);
    setCreatedAccount(null);
    recon.setSavedReconPaths([]);
    clearAllErrors();
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    clearFieldError(name);
  };

  const updateType = (type) => {
    setForm((current) => ({ ...current, type }));
    clearFieldError('type');
  };

  const updateAuthenticationMode = (authenticationMode) => {
    setForm((current) => {
      let password = '';
      let privateKey = '';
      let publicKey = '';
      if (authenticationMode === SFTP_AUTHENTICATION_MODE.PASSWORD) {
        password = current.password;
      }
      if (authenticationMode === SFTP_AUTHENTICATION_MODE.SSH_KEY) {
        privateKey = current.private_key;
        publicKey = current.public_key;
      }

      return {
        ...current,
        authentication_mode: authenticationMode,
        password,
        private_key: privateKey,
        public_key: publicKey,
      };
    });
    clearFieldError('authentication_mode');
    clearFieldError('password');
    clearFieldError('private_key');
    clearFieldError('public_key');
  };

  const updateShouldEncrypt = (shouldEncrypt) => {
    setForm((current) => ({
      ...current,
      should_encrypt: shouldEncrypt,
      pgp_public_key: shouldEncrypt ? current.pgp_public_key : '',
    }));
    clearFieldError('should_encrypt');
    clearFieldError('pgp_public_key');
  };

  const saveConfiguration = (managedAccountId) => {
    if (form.type === MANAGED_ACCOUNT_TYPE.POSITIVE_PAY) {
      return ManagedAccountsApi.updatePositivePay(managedAccountId, {
        ...toSftpPayload(form),
        outbound_path: form.outbound_path,
        positive_pay_prefix: form.positive_pay_prefix,
        header_account: form.header_account,
        should_encrypt: form.should_encrypt,
        pgp_public_key: form.pgp_public_key,
      });
    }

    return ManagedAccountsApi.updatePaymentsManager(managedAccountId, {
      ...toSftpPayload(form),
      outbound_path: form.outbound_path,
      inbound_path: form.inbound_path,
      recon_paths: recon.reconPathValues(),
      payments_manager_prefix: form.payments_manager_prefix,
      ack_prefix: form.ack_prefix,
      notification_prefix: form.notification_prefix,
      recon_prefix: form.recon_prefix,
    });
  };

  const save = async (event) => {
    event.preventDefault();
    clearAllErrors();
    setSaving(true);

    try {
      let account = createdAccount;

      if (!account) {
        const response = await ManagedAccountsApi.create({
          name: form.name,
          provider: form.provider,
          type: form.type,
          ...toSftpPayload(form),
        });
        account = response.data.data;
        setCreatedAccount(account);
      }

      await saveConfiguration(account.id);
      toast.success('Managed account created');
      reset();
      onCreated(account);
    } catch (error) {
      setBackendErrors(error.response?.data);
      if (!error.response?.data?.message) {
        toast.error('Failed to create the managed account');
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    fieldErrors,
    generalErrors,
    saving,
    pendingConfigurationFor: createdAccount,
    reconPaths: recon.reconPaths,
    updateField,
    updateType,
    updateAuthenticationMode,
    updateShouldEncrypt,
    addReconPath: recon.addReconPath,
    updateReconPath: recon.updateReconPath,
    removeReconPath: recon.removeReconPath,
    reset,
    save,
  };
};
